import React from 'react';
import * as Yup from 'yup';
import PropTypes from 'prop-types';
import request from '../../utils/request';
import { ModalFieldLabel } from './css';
import DropdownCheckbox from '../../form/fields/DropdownCheckbox';

const getDealershipList = list => {
  const updatedList =
    list &&
    list.length &&
    list.map(dealership => {
      return {
        label: dealership.name,
        value: dealership.id
      };
    });
  return updatedList;
};

// Soft-deleted dealerships / disabled channels can't be picked for offers (the backend's
// "Select All" sync skips them too). Older backends don't send isDeleted, so nothing is hidden.
const getActiveList = list => getDealershipList((list || []).filter(item => !item.isDeleted));

export const getDealerships = (
  setDealershipList,
  setChannels,
  setIsLoading
) => {
  request.get('/get-wln-filters').then(response => {
    if (response && response.status === 200) {
      if (
        response.data &&
        response.data.channels &&
        response.data.channels.length
      ) {
        setChannels(getActiveList(response.data.channels));
      }
      if (
        response.data &&
        response.data.dealerships &&
        response.data.dealerships.length
      ) {
        setDealershipList(getActiveList(response.data.dealerships));
        setIsLoading(false);
      }
    }
  });
};

export const getTableData = ({
  setTableData,
  setIsLoadingTable,
  limit,
  offerPage
}) => {
  request
    .get(`/offers?page=${offerPage || 1}&limit=${limit}`)
    .then(response => {
      setTableData({ orders: response.data.rows, count: response.data.count });
      setIsLoadingTable(false);
    });
};

export const getFilterTableData = ({
  setTableData,
  setIsLoadingTable,
  limit,
  offerPage,
  filters
}) => {
  request
    .post(`/offers/filter?page=${offerPage || 1}&limit=${limit}`, filters)
    .then(response => {
      setTableData({ orders: response.data.rows, count: response.data.count });
      setIsLoadingTable(false);
    });
};

/** Fetch all offer codes for the "Offer Codes Not Stackable" dropdown (no pagination). */
export const getOfferCodesForDropdown = (setOfferCodes, setIsLoading) => {
  if (setIsLoading) setIsLoading(true);
  request
    .post(`/offers/filter?limit=0`, { showExpired: false })
    .then(response => {
      const rows = response.data.rows || [];
      const byCode = new Map();
      rows.forEach(row => {
        if (row.offerCode) {
          byCode.set(row.offerCode, { label: row.offerCode, value: row.offerCode });
        }
      });
      setOfferCodes(Array.from(byCode.values()));
    })
    .catch(() => setOfferCodes([]))
    .finally(() => {
      if (setIsLoading) setIsLoading(false);
    });
};

/**
 * True when every available option is selected ("Select All"). The backend then
 * keeps the offer linked to any channel/dealership added in the future.
 * Returns undefined when options aren't loaded so the stored flag is left unchanged.
 */
export const isAllOptionsSelected = (selected, options) => {
  if (!Array.isArray(options) || options.length === 0) return undefined;
  const selectedValues = selected || [];
  return options.every(opt => selectedValues.includes(opt.value));
};

/** Return the channel option that represents "Dealer" (label contains 'dealer'). */
export const getDealerChannelOption = (channelOptions) =>
  (channelOptions || []).find((c) => (c.label || '').toLowerCase().includes('dealer'));

/** True if the selected channels include the Dealer channel (so Dealership is required). */
export const isDealerChannelSelected = (selectedChannels, channelOptions) => {
  const dealer = getDealerChannelOption(channelOptions);
  if (!dealer) return false;
  const selectedIds = (selectedChannels || []).map((ch) =>
    ch && (ch.value !== undefined ? ch.value : ch.id !== undefined ? ch.id : ch)
  );
  return selectedIds.indexOf(dealer.value) !== -1;
};

export const validationSchema = (isServicesVisible, channelOptions = []) =>
  Yup.object({
    frequency: Yup.string().when([], {
      is: () => isServicesVisible,
      then: schema => schema.required('Field is required'),
      otherwise: schema => schema.notRequired()
    }),
    value: Yup.number().when([], {
      is: () => isServicesVisible,
      then: schema => schema.required('Field is required'),
      otherwise: schema => schema.notRequired()
    }),
    offerDetailsEn: Yup.string().when([], {
      is: () => isServicesVisible,
      then: schema => schema.required('Field is required'),
      otherwise: schema => schema.notRequired()
    }),
    province: Yup.array().when([], {
      is: () => isServicesVisible,
      then: schema => schema.notRequired(),
      otherwise: schema => schema.min(1, 'Field is required')
    }),
    channels: Yup.array().when([], {
      is: () => isServicesVisible,
      then: schema => schema.notRequired(),
      otherwise: schema => schema.min(1, 'Field is required')
    }),
    dealerships: Yup.array()
      .when(['channels'], {
        is: (channelsSelected) => {
          if (isServicesVisible) return false;
          return isDealerChannelSelected(channelsSelected, channelOptions);
        },
        then: (schema) => schema.min(1, 'Field is required'),
        otherwise: (schema) => schema.notRequired()
      }),
    product: Yup.array().when([], {
      is: () => isServicesVisible,
      then: schema => schema.notRequired(),
      otherwise: schema => schema.min(1, 'Field is required')
    }),
    term: Yup.array().when([], {
      is: () => isServicesVisible,
      then: schema => schema.notRequired(),
      otherwise: schema => schema.required('Field is required')
    }),
    couponBasedDiscount: Yup.string().when([], {
      is: () => isServicesVisible,
      then: schema => schema.notRequired(),
      otherwise: schema => schema.required('Field is required')
    }),
    offerCode: Yup.string().when([], {
      is: () => isServicesVisible,
      then: schema => schema.notRequired(),
      otherwise: schema => schema.required('Field is required')
    }),
    validationCode: Yup.string().when(['couponBasedDiscount'], {
      is: couponBasedDiscount => !isServicesVisible && couponBasedDiscount === 'Yes',
      then: schema => schema.required('Field is required'),
      otherwise: schema => schema.notRequired()
    }),
    bundled: Yup.array().when([], {
      is: () => isServicesVisible,
      then: schema => schema.notRequired(),
      otherwise: schema => schema.required('Field is required')
    }),
    // T016 [US3]: offerType is mandatory; determines tab in sales rep offer table
    offerType: Yup.string().when([], {
      is: () => isServicesVisible,
      then: schema => schema.notRequired(),
      otherwise: schema => schema.required('Field is required')
    }),
    // T020 [US4]: tsbPlan required only when product contains 'TSB'
    tsbPlan: Yup.array().when(['product'], {
      is: (product) => !isServicesVisible && Array.isArray(product) && product.includes('TSB'),
      then: schema => schema.min(1, 'Field is required'),
      otherwise: schema => schema.notRequired()
    }),
    expiredAt: Yup.date().when([], {
      is: () => isServicesVisible,
      then: schema => schema.notRequired(),
      otherwise: schema => schema.required('Field is required')
    }),
    // descriptionEn: Yup.string().when([], {
    //   is: () => isServicesVisible,
    //   then: schema => schema.notRequired(),
    //   otherwise: schema => schema.required('Field is required')
    // })
  });

export const RenderDynamicField = ({
  field,
  values,
  errors,
  setFieldValue,
  state,
  handleWheel,
  FormikField
}) => {
  const isDealershipRequiredWhenDealerChannel =
    field.name === 'dealerships' &&
    isDealerChannelSelected(values.channels, state.channels);
  const isRequired =
    isDealershipRequiredWhenDealerChannel ||
    (field.name !== 'dealerships' && field.isRequired) ||
    (field.name === 'validationCode' && values.couponBasedDiscount === 'Yes');

  return (
    <div key={field.name}>
      <ModalFieldLabel htmlFor={field.name}>
        {field.label} {isRequired ? '*' : ''}
      </ModalFieldLabel>

      {(() => {
        switch (field.type) {
          case 'multiSelect':
            return (
              <DropdownCheckbox
                id={field.name}
                name={field.name}
                options={
                  field.optionsKey ? state[field.optionsKey] : field.options
                }
                value={values[field.name] || []}
                onChange={(selectedValues) => {
                  setFieldValue(field.name, selectedValues);
                }}
                placeholder="Select options"
              />
            );

          case 'dropdown':
            return (
              <select
                id={field.name}
                name={field.name}
                className="frg-input-field color-light"
                style={{ width: '100%' }}
                onChange={e => setFieldValue(field.name, e.target.value)}
                value={values[field.name]}
              >
                {!values[field.name] && (
                  <option value="">Select {field.label}</option>
                )}
                {field.options.map(opt => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            );

          case 'text':
          default:
            return (
              <FormikField
                type={field.type}
                id={field.name}
                name={field.name}
                className="frg-input-field color-light"
                style={{ width: '100%' }}
                value={values[field.name]}
                onWheel={handleWheel}
                onChange={e => setFieldValue(field.name, e.target.value)}
                disabled={field.isDisabled}
                onClick={e => {
                  if (field.type === 'date') {
                    e.target.showPicker(); // ← open calendar when clicking the input field
                  }
                }}
              />
            );
        }
      })()}

      {errors[field.name] && (
        <div style={{ color: 'red', fontSize: '16px' }}>
          {errors[field.name]}
        </div>
      )}
    </div>
  );
};

RenderDynamicField.propTypes = {
  field: PropTypes.shape({
    name: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    type: PropTypes.string,
    isRequired: PropTypes.bool,
    isDisabled: PropTypes.bool,
    options: PropTypes.arrayOf(
      PropTypes.shape({
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
          .isRequired,
        label: PropTypes.string.isRequired
      })
    ),
    optionsKey: PropTypes.string
  }).isRequired,
  values: PropTypes.objectOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.array])
  ).isRequired,
  state: PropTypes.objectOf(
    PropTypes.arrayOf(
      PropTypes.shape({
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
          .isRequired,
        label: PropTypes.string.isRequired
      })
    )
  ),
  errors: PropTypes.objectOf(PropTypes.string),
  setFieldValue: PropTypes.func.isRequired,
  handleWheel: PropTypes.func,
  FormikField: PropTypes.elementType.isRequired
};
